import { useEffect, useMemo, useRef, type ReactElement } from 'react';
import { ChevronRight, ListTree, PanelRightClose, PanelRightOpen } from 'lucide-react';
import type { HeadingItem } from '@slate/core';
import { useRegisterFocusZone } from '../focus';
import { useOutlineSpeedSearch, type OutlineRow } from './useOutlineSpeedSearch';
import { HighlightedLabel } from './HighlightedLabel';

/** A heading with its flat document index (for wysiwyg heading-index jumps). */
export interface OutlineHeading extends HeadingItem {
  /** 0-based index of this heading among all headings, in document order. */
  index: number;
}

interface OutlineNode extends OutlineHeading {
  children: OutlineNode[];
}

export interface OutlinePanelProps {
  /** Headings of the active document, in document order. */
  headings: OutlineHeading[];
  /** Whether the panel is docked (pinned) as a column vs. a hover overlay. */
  pinned: boolean;
  onTogglePin: () => void;
  /** Jump every visible pane to this heading. */
  onJump: (heading: OutlineHeading) => void;
  /** Slug of the heading nearest the top of the active view (highlight). */
  activeSlug?: string | null;
}

/**
 * Build a nested heading tree from a flat, document-ordered heading list.
 * Nesting is by `depth`: a heading becomes a child of the most recent shallower
 * heading. Skipped levels (e.g. h1 → h3) are tolerated — h3 just nests under h1.
 */
function buildTree(headings: OutlineHeading[]): OutlineNode[] {
  const roots: OutlineNode[] = [];
  const stack: OutlineNode[] = [];
  for (const h of headings) {
    const node: OutlineNode = { ...h, children: [] };
    while (stack.length > 0 && stack[stack.length - 1].depth >= h.depth) stack.pop();
    if (stack.length === 0) roots.push(node);
    else stack[stack.length - 1].children.push(node);
    stack.push(node);
  }
  return roots;
}

/**
 * Slugs of the ancestor headings of `activeSlug` (its parent trail, excluding
 * the active heading itself). Used to subtly mark the path to the active item.
 * Built from the flat list using the same depth-stack as {@link buildTree}.
 */
function ancestorTrail(headings: OutlineHeading[], activeSlug: string | null | undefined): Set<string> {
  const trail = new Set<string>();
  if (!activeSlug) return trail;
  const stack: OutlineHeading[] = [];
  for (const h of headings) {
    while (stack.length > 0 && stack[stack.length - 1].depth >= h.depth) stack.pop();
    if (h.slug === activeSlug) {
      for (const a of stack) trail.add(a.slug);
      break;
    }
    stack.push(h);
  }
  return trail;
}

/** Stable per-document id for a heading row. */
const headingId = (h: OutlineHeading): string => `${h.slug}:${h.index}`;

/** Top + bottom safety margin (px) kept clear of the body's edges when the
 *  active row is scrolled into view — gives the user enough surrounding
 *  context to read the active heading's position in the tree. */
const OUTLINE_SCROLL_PADDING = 64;

interface RowProps {
  row: OutlineRow<OutlineNode>;
  active: boolean;
  inTrail: boolean;
  onJump: (heading: OutlineHeading) => void;
  onToggle: (id: string) => void;
}

function HeadingRow({ row, active, inTrail, onJump, onToggle }: RowProps): ReactElement {
  const { node, id, level, hasChildren, open, isMatch, isFocused, range } = row;
  return (
    <div
      className={'mp-outline__row' + (isMatch ? ' mp-outline__row--match' : '')}
      data-outline-id={id}
      data-active={active ? '' : undefined}
      data-trail={inTrail ? '' : undefined}
      data-focused={isFocused ? '' : undefined}
      aria-current={active ? 'location' : undefined}
      aria-selected={isFocused ? true : undefined}
      role="treeitem"
      aria-expanded={hasChildren ? open : undefined}
      aria-level={level + 1}
      tabIndex={0}
      style={{ paddingLeft: `${8 + level * 14}px` }}
      title={node.text}
      onClick={() => onJump(node)}
    >
      {hasChildren ? (
        <button
          type="button"
          className="mp-outline__chevron"
          data-open={open ? '' : undefined}
          aria-label={open ? 'Collapse' : 'Expand'}
          title={open ? 'Collapse' : 'Expand'}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(id);
          }}
        >
          <ChevronRight size={13} aria-hidden strokeWidth={2.25} />
        </button>
      ) : (
        <span className="mp-outline__chevron-gap" aria-hidden />
      )}
      <span className="mp-outline__label" data-depth={node.depth}>
        <HighlightedLabel text={node.text || 'Untitled heading'} range={range} />
      </span>
    </div>
  );
}

/**
 * Right-edge document outline (heading hierarchy). Collapsible tree with
 * IntelliJ-style speed search (type to highlight + cycle through matches);
 * clicking a heading jumps every visible pane to it (see App's `onJump`). The
 * reveal/pin behaviour (hover-overlay vs. docked column) is owned by `App` —
 * this component only renders the panel chrome + tree and a pin toggle.
 */
export function OutlinePanel({
  headings,
  pinned,
  onTogglePin,
  onJump,
  activeSlug,
}: OutlinePanelProps): ReactElement {
  const tree = useMemo(() => buildTree(headings), [headings]);
  const trail = useMemo(() => ancestorTrail(headings, activeSlug), [headings, activeSlug]);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // The outline is a focus zone (⌥⌘→ / F6 cycle land here). Its first heading
  // row / button is the focus target. Always considered visible here — App only
  // mounts OutlinePanel for markdown docs, and the cycle skips it otherwise.
  const outlineRef = useRef<HTMLDivElement>(null);
  useRegisterFocusZone(outlineRef, 'outline', '[data-focus-primary], button', {
    label: 'Outline',
    order: 25,
    isVisible: () => true,
  });

  const speed = useOutlineSpeedSearch<OutlineNode>({
    roots: tree,
    getId: headingId,
    getLabel: (h) => h.text,
    getChildren: (h) => h.children,
    onActivate: (h) => onJump(h),
    bodyRef,
  });

  // Scroll-spy: keep the active heading inside the outline's scroll viewport as
  // the user scrolls the document. We only scroll when the row is currently
  // outside a padded "safe zone" near the body's edges — no scroll churn while
  // the active heading is already comfortably visible. Skipped while a speed-
  // search query is active (the focused match owns scrolling then).
  useEffect(() => {
    if (speed.active) return;
    if (!activeSlug) return;
    const body = bodyRef.current;
    if (!body) return;
    const row = body.querySelector<HTMLElement>('.mp-outline__row[data-active]');
    if (!row) return;
    const bodyRect = body.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const topGap = rowRect.top - bodyRect.top;
    const bottomGap = bodyRect.bottom - rowRect.bottom;
    // Already visible with padding to spare — leave the user's view alone.
    if (topGap >= OUTLINE_SCROLL_PADDING && bottomGap >= OUTLINE_SCROLL_PADDING) return;
    // Target: align the row's top with `body.top + padding`, clamped to the
    // body's scrollable range.
    const offsetWithinBody = topGap + body.scrollTop;
    const maxScrollTop = Math.max(0, body.scrollHeight - body.clientHeight);
    const targetScrollTop = Math.min(
      Math.max(0, offsetWithinBody - OUTLINE_SCROLL_PADDING),
      maxScrollTop,
    );
    body.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
  }, [activeSlug, speed.active, speed.rows]);

  return (
    <div
      className="mp-outline__inner"
      role="tree"
      aria-label="Document outline"
      data-focus-zone="outline"
      ref={outlineRef}
    >
      <div className="mp-outline__header">
        <span className="mp-outline__title">
          <ListTree size={14} aria-hidden strokeWidth={2} />
          Outline
        </span>
        <button
          type="button"
          className="mp-iconbtn"
          aria-pressed={pinned}
          aria-label={pinned ? 'Unpin outline' : 'Pin outline'}
          title={pinned ? 'Unpin outline' : 'Pin outline'}
          onClick={onTogglePin}
        >
          {pinned ? <PanelRightClose size={15} aria-hidden /> : <PanelRightOpen size={15} aria-hidden />}
        </button>
      </div>
      <div
        ref={bodyRef}
        className="mp-outline__body"
        tabIndex={0}
        onKeyDown={speed.handleKeyDown}
        onBlur={speed.onBlur}
      >
        {speed.active && (
          <div
            className="mp-outline__speedsearch"
            data-no-match={speed.matchCount === 0 ? '' : undefined}
            role="status"
            aria-live="polite"
          >
            {speed.query}
          </div>
        )}
        {speed.rows.length === 0 ? (
          <p className="mp-outline__empty">No headings</p>
        ) : (
          speed.rows.map((row) => (
            <HeadingRow
              key={row.id}
              row={row}
              active={!speed.active && activeSlug === row.node.slug}
              inTrail={!speed.active && activeSlug !== row.node.slug && trail.has(row.node.slug)}
              onJump={onJump}
              onToggle={speed.toggleCollapse}
            />
          ))
        )}
      </div>
    </div>
  );
}
